import { apiFetch } from '@/api/client'
import type { MemberDetail } from '@/data/api'
import type { Account, Sport } from '@/data/types'
import type { AgentNode } from '@/api/map/agent'
import { fromMinorUnits } from '@/api/map/agent'
import { fromMinorUnitsSigned } from '@/api/map/pnl'
import { enrichTreeAccounts, type AnalyticsProjected } from '@/api/map/downline'

/** Lab `Member/searchHierarchyNew` request — Agent-API `POST /v1/member/searchHierarchyNew`. */
export type SearchHierarchyNewRequest = {
  /** Username prefix (defaults to `a` when empty) or exact matches when an array. */
  filter?: string | string[]
  page?: number
  noPlayer?: boolean
  onlyOneLevel?: boolean
  parentId?: string
  /** Legacy Lab field — ignored by hierarchy-service. */
  accessLevel?: string
}

/** One autocomplete row (prefix search). */
export type SearchHierarchyNewRow = {
  username: string
}

/** Lab User-API callback envelope. */
export type SearchHierarchyNewResponse = {
  data: { data: SearchHierarchyNewRow[] }
  value: boolean
}

/** POST hierarchy member autocomplete — Lab workstation `getRecords` / ui-select. */
export async function searchHierarchyNew(
  body: SearchHierarchyNewRequest,
): Promise<SearchHierarchyNewRow[]> {
  const res = await apiFetch<SearchHierarchyNewResponse>('/v1/member/searchHierarchyNew', {
    method: 'POST',
    body: JSON.stringify(body),
  })
  if (!res.value) return []
  return res.data?.data ?? []
}

/** Map Lab callback rows to plain usernames for datalist / chip pickers. */
export function searchHierarchyNewUsernames(rows: SearchHierarchyNewRow[]): string[] {
  return rows.map((row) => row.username).filter(Boolean)
}

/** Agent-API Lab-compatible `Member/getAllTm` row. */
export type GetAllTmRow = {
  _id: string
  username: string
}

export type GetAllTmResponse = {
  data: GetAllTmRow[]
  value: boolean
}

/** Normalize the Lab callback and discard malformed picker entries. */
export function rowsFromGetAllTm(response: GetAllTmResponse): GetAllTmRow[] {
  if (!response.value) return []
  return (response.data ?? []).filter((row) => Boolean(row._id && row.username))
}

/** Company Master Triple Master picker — Agent-API hierarchy service. */
export async function getAllTm(name = ''): Promise<GetAllTmRow[]> {
  const response = await apiFetch<GetAllTmResponse>('/v1/member/getAllTm', {
    method: 'POST',
    body: JSON.stringify({ name }),
  })
  return rowsFromGetAllTm(response)
}

/** 7 rows (Mon–Sun) × 24 hours of zeros — the shape the heatmap expects. */
function emptyActivity(): number[][] {
  return Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0))
}

/** analytics-service `GET /members/{id}/detail` (OpenAPI). */
export interface AnalyticsMemberDetail {
  asOf?: string
  martBuiltAt?: string | null
  martStale?: boolean
  memberId: string
  name: string
  parentId?: string
  createdAt?: string
  creditLimitMinor?: string
  balanceMinor: string
  exposure: AnalyticsProjected
  period?: {
    turnoverMinor?: string
    betsPlaced?: number
    betsSettled?: number
    memberNetMinor?: string
    bookNetMinor?: string
  }
  trend?: {
    date: string
    betsPlaced?: number
    turnoverMinor?: string
    betsSettled?: number
    bookNetMinor?: string
    activeMembers?: number
  }[]
  lastBetDate?: string | null
  hasEverSignedIn?: boolean | null
}

/**
 * MemberDetail from the hierarchy tree.
 *
 * Identity, balance, credit limit, share, member counts and the upline chain are
 * real (hierarchy-service). Period analytics come from analytics-service when live.
 */
export function buildMemberDetail(
  nodes: AgentNode[],
  memberId: string,
  _selfId: string,
): MemberDetail | null {
  const accounts = enrichTreeAccounts(nodes)
  const byId = new Map(accounts.map((a) => [a.id, a]))
  const account = byId.get(memberId)
  if (!account) return null

  const upline: Account[] = []
  const seen = new Set<string>()
  let cursor = account.parentId
  while (cursor && !seen.has(cursor)) {
    seen.add(cursor)
    const parent = byId.get(cursor)
    if (!parent) break
    upline.unshift(parent)
    cursor = parent.parentId
  }

  return {
    account,
    upline,
    daily: [],
    bySport: [],
    activity: emptyActivity(),
    stakeBuckets: [],
    lifetime: {
      turnover: 0,
      memberPnl: 0,
      commission: 0,
      bets: 0,
      settledBets: 0,
      wins: 0,
      avgStake: 0,
      biggestWin: 0,
      biggestLoss: 0,
    },
    openExposure: account.exposure,
  }
}

/**
 * Overlay analytics `GET /members/{id}/detail` onto hierarchy MemberDetail.
 * Upline stays hierarchy; period/trend/lifetime come from the mart contract.
 */
export function mergeAnalyticsMemberDetail(
  base: MemberDetail,
  live: AnalyticsMemberDetail,
): MemberDetail {
  const exposure =
    live.exposure.stale || live.exposure.valueMinor == null
      ? 0
      : Math.max(0, fromMinorUnitsSigned(live.exposure.valueMinor))

  const account: Account = {
    ...base.account,
    username: live.name || base.account.username,
    displayName: live.name || base.account.displayName,
    balance: fromMinorUnitsSigned(live.balanceMinor),
    creditLimit:
      live.creditLimitMinor != null
        ? fromMinorUnits(live.creditLimitMinor)
        : base.account.creditLimit,
    distributedBalance: base.account.distributedBalance,
    exposure,
    createdAt: live.createdAt || base.account.createdAt,
    lastActiveAt: live.lastBetDate ? `${live.lastBetDate}T00:00:00.000Z` : base.account.lastActiveAt,
  }

  const period = live.period
  const turnover = fromMinorUnits(period?.turnoverMinor)
  const memberPnl = fromMinorUnitsSigned(period?.memberNetMinor)
  const betsPlaced = period?.betsPlaced ?? 0
  const betsSettled = period?.betsSettled ?? 0

  const daily = (live.trend ?? []).map((d) => ({
    date: d.date,
    turnover: fromMinorUnits(d.turnoverMinor),
    memberPnl: fromMinorUnitsSigned(d.bookNetMinor) * -1,
    bets: d.betsSettled ?? d.betsPlaced ?? 0,
  }))

  return {
    account,
    upline: base.upline,
    daily,
    bySport: [] as { sport: Sport; turnover: number; memberPnl: number; bets: number }[],
    activity: emptyActivity(),
    stakeBuckets: [],
    lifetime: {
      turnover,
      memberPnl,
      commission: 0,
      bets: betsPlaced,
      settledBets: betsSettled,
      wins: 0,
      avgStake: betsSettled > 0 ? turnover / betsSettled : 0,
      biggestWin: 0,
      biggestLoss: 0,
    },
    openExposure: exposure,
  }
}

/** Enriched account (with child/member counts) for one id, from the tree. */
export function findEnrichedAccount(nodes: AgentNode[], id: string): Account | null {
  return enrichTreeAccounts(nodes).find((a) => a.id === id) ?? null
}

/**
 * Overlay a single-node read onto the tree list.
 *
 * `GET /agents/{id}/tree` omits `balance`; only `GET /agents/{id}` returns it.
 */
export function overlayNode(nodes: AgentNode[], node: AgentNode): AgentNode[] {
  if (!nodes.some((n) => n.id === node.id)) return [...nodes, node]
  return nodes.map((n) => (n.id === node.id ? { ...n, ...node } : n))
}
